import * as YAML from "yaml";
import * as adapter from "./modelYamlImportAdapter";
import * as accessor from "./nodeAccessor";
import * as factory from "./nodeFactory";
import * as nodeUtils from "./utils/nodeUtils";
import * as pathUtils from "./utils/pathUtils";
import * as systemUtils from "./utils/systemUtils";
import * as typeUtils from "./utils/typeUtils";
import * as uuidUtils from "./utils/uuidUtils";

//----------------------------------------------------------------------------------

export const parseYamlModel = (node) => {
  parseRoot(node);

  const _globals = parseGlobals(node);
  const _functions = parseFunctions(node);

  handleGlobalParameters(_globals);
  handleLinkedParameters(_globals, _functions);

  updateReferenceCount(_globals, _functions);

  nodeUtils.visitElements(node, (e) => set(e));
};

//----------------------------------------------------------------------------------

const set = (node) => {
  nodeUtils.setUUID(node, uuidUtils.createUuid());
  nodeUtils.setTimeCreate(node, 0);
  nodeUtils.setTimeUpdate(node, Date.now());

  return node;
};

//----------------------------------------------------------------------------------

const parseRoot = (root) => {
  adapter.adaptYamlNodeRoot(root);

  accessor.setID(root, typeUtils.getTypeRoot());
  accessor.setName(root, typeUtils.getTypeRoot());
  accessor.setType(root, typeUtils.getTypeRoot());
};

const parseGlobals = (root) => {
  const _reference = {};

  parseContainer(root, null, _reference);

  return _reference;
};

const parseFunctions = (root) => {
  const _reference = {};

  nodeUtils.getFunctions(root).forEach((e) => {
    nodeUtils.setType(e, typeUtils.getTypeFunction());
    parseContainer(e, null, _reference);
  });

  validateChildrenFunctions(root);

  return _reference;
};

const parseContainer = (node, parentId, reference = null) => {
  adapter.adaptYamlNodeContainer(node);

  reference = reference ?? {};

  parseType(node);
  parseConditions(node);

  validateNode(node, parentId);

  nodeUtils.updateIdNode(node, parentId);

  updateReference(node, reference);

  if (nodeUtils.hasParameters(node) && nodeUtils.hasChoices(node)) {
    throw new Error(`Node '${nodeUtils.getName(node)}' contains both parameters and choices.`);
  }

  updateChildrenParameters(node, reference);
  updateChildrenChoices(node, reference);
  updateChildrenConditions(node, reference);

  return reference;
};

const parseType = (node) => {
  const _types = typeUtils.getExternalModelTypes();

  for (let i = 0; i < _types.length; i++) {
    const _typeLocal = _types[i];

    if (node[_typeLocal]) {
      nodeUtils.setName(node, node[_typeLocal]);
      nodeUtils.setType(node, _typeLocal);

      delete node[_typeLocal];

      return;
    }
  }
};

const parseConditions = (node) => {
  const _conditions = accessor.getConditions(node);

  if (_conditions.length > 0) {
    const _conditionsParsed = [];

    _conditions.forEach((e) => {
      const _condition = systemUtils.createDeepCopy(e);
      adapter.adaptYamlNodeCondition(_condition);

      parseCondition(_condition, typeUtils.getTypeConditionAlias());
      parseCondition(_condition, typeUtils.getTypeConditionConstraint());
      parseCondition(_condition, typeUtils.getTypeConditionAssignment());

      _conditionsParsed.push(_condition);
    });

    accessor.setConditions(node, _conditionsParsed);
  } else {
    nodeUtils.setConditions(node, null);
  }
};

const parseCondition = (condition, type) => {
  if (condition[type]) {
    nodeUtils.setType(condition, type);
    nodeUtils.setName(condition, condition[type]);

    delete condition[type];
  }
};

const updateReference = (node, reference) => {
  if (!reference) {
    return;
  }

  const _type = nodeUtils.getType(node);

  if (_type) {
    if (!reference[_type]) {
      reference[_type] = [];
    }
    reference[_type].push(node);
  }
};

const updateChildrenParameters = (node, reference) => {
  const _parameters = nodeUtils.getParameters(node);
  const _idTarget = nodeUtils.getID(node);

  _parameters.forEach((e) => parseContainer(e, _idTarget, reference));

  validateChildrenParameters(node);
};

const updateChildrenConditions = (node, reference) => {
  const _conditions = nodeUtils.getConditions(node);
  const _idTarget = nodeUtils.getID(node);

  _conditions.forEach((e) => parseContainer(e, _idTarget, reference));

  validateChildrenConditions(node);
};

const updateChildrenChoices = (node, reference) => {
  const _choices = nodeUtils.getChoices(node);
  const _idTarget = nodeUtils.getID(node);

  const _choicesParsed = updateChildrenChoicesArray(_choices, _idTarget);
  _choicesParsed.forEach((e) => parseContainer(e, _idTarget, reference));
  nodeUtils.setChoices(node, _choicesParsed);

  validateChildrenChoices(node);
};

const updateChildrenChoicesArray = (choices, idTarget) => {
  if (choices.length === 0) {
    return choices;
  }

  if (!systemUtils.isObject(choices[0])) {
    return choices.map((x) => factory.createChoice(idTarget, x));
  } else {
    return choices;
  }
};

//----------------------------------------------------------------------------------

const validateChildrenFunctions = (node) => {
  const _names = nodeUtils.getFunctions(node).map((e) => nodeUtils.getName(e));

  if (_names.length === 0) {
    return;
  }

  const _namesSet = new Set();

  _names.forEach((e) => {
    if (_namesSet.has(e)) {
      throw new Error(`The root node contains a duplicate function name '${e}'.`);
    }

    _namesSet.add(e);
  });
};

const validateChildrenParameters = (node) => {
  const _typesSet = new Set(nodeUtils.getParameters(node).map((e) => nodeUtils.getType(e)));

  if (_typesSet.has(typeUtils.getTypeChoice())) {
    _typesSet.delete(typeUtils.getTypeChoice());

    if (_typesSet.size > 0) {
      throw new Error(`Node '${nodeUtils.getName(node)}' contains an inconsistent parameter type.`);
    }
  } else {
    _typesSet.delete(typeUtils.getTypeParameter());
    _typesSet.delete(typeUtils.getTypeParameterGlobal());
    _typesSet.delete(typeUtils.getTypeParameterLinked());
    _typesSet.delete(typeUtils.getTypeParameterOutput());

    if (_typesSet.size > 0) {
      throw new Error(`Node '${nodeUtils.getName(node)}' contains an inconsistent parameter type.`);
    }
  }

  const _names = nodeUtils.getParameters(node).map((e) => nodeUtils.getName(e));

  if (_names.length === 0) {
    return;
  }

  const _namesSet = new Set();

  _names.forEach((e) => {
    if (_namesSet.has(e)) {
      if (nodeUtils.isRoot(node)) {
        throw new Error(`The root contains a duplicate global parameter '${e}'.`);
      }

      if (nodeUtils.isFunction(node)) {
        throw new Error(
          `The function '${nodeUtils.getName(node)}' contains a duplicate parameter '${e}'.`
        );
      }

      if (nodeUtils.isParameterTopGlobal(node)) {
        throw new Error(
          `The global parameter '${nodeUtils.getName(node)}' contains a duplicate parameter '${e}'.`
        );
      }

      if (nodeUtils.isParameterNestedGlobal(node)) {
        throw new Error(
          `The global nested parameter '${nodeUtils.getName(
            node
          )}' contains a duplicate parameter '${e}'.`
        );
      }

      if (nodeUtils.isParameterTopLocal(node)) {
        throw new Error(
          `The function parameter '${nodeUtils.getName(
            node
          )}' contains a duplicate parameter '${e}'.`
        );
      }

      if (nodeUtils.isParameterNestedLocal(node)) {
        throw new Error(
          `The function nested parameter '${nodeUtils.getName(
            node
          )}' contains a duplicate parameter '${e}'.`
        );
      }

      throw new Error(`Node '${nodeUtils.getName(node)}' contains a duplicate parameter '${e}'.`);
    }

    _namesSet.add(e);
  });
};

const validateChildrenChoices = (node) => {
  const _typesSet = new Set(nodeUtils.getChoices(node).map((e) => nodeUtils.getType(e)));

  _typesSet.delete(typeUtils.getTypeChoice());

  if (_typesSet.size > 0) {
    throw new Error(`Node '${nodeUtils.getName(node)}' contains an inconsistent choice type.`);
  }

  const _names = nodeUtils.getChoices(node).map((e) => nodeUtils.getName(e));

  if (_names.length === 0) {
    return;
  }

  const _namesSet = new Set();

  _names.forEach((e) => {
    if (_namesSet.has(e)) {
      if (nodeUtils.isParameterTopGlobal(node)) {
        throw new Error(
          `The global parameter '${nodeUtils.getName(node)}' contains a duplicate choice '${e}'.`
        );
      }

      if (nodeUtils.isParameterNestedGlobal(node)) {
        throw new Error(
          `The global nested parameter '${nodeUtils.getName(
            node
          )}' contains a duplicate choice '${e}'.`
        );
      }

      if (nodeUtils.isParameterTopLocal(node)) {
        throw new Error(
          `The function parameter '${nodeUtils.getName(node)}' contains a duplicate choice '${e}'.`
        );
      }

      if (nodeUtils.isParameterNestedLocal(node)) {
        throw new Error(
          `The function nested parameter '${nodeUtils.getName(
            node
          )}' contains a duplicate choice '${e}'.`
        );
      }

      if (nodeUtils.isChoice(node)) {
        throw new Error(
          `The abstract choice '${nodeUtils.getName(node)}' contains a duplicate choice '${e}'.`
        );
      }

      throw new Error(`Node '${nodeUtils.getName(node)}' contains a duplicate choice '${e}'.`);
    }

    _namesSet.add(e);
  });
};

const validateChildrenConditions = (node) => {
  const _names = nodeUtils.getConditions(node).map((e) => nodeUtils.getName(e));

  if (_names.length === 0) {
    return;
  }

  const _namesSet = new Set();

  _names.forEach((e) => {
    if (_namesSet.has(e)) {
      if (nodeUtils.isFunction(node)) {
        throw new Error(
          `The function '${nodeUtils.getName(node)}' contains a duplicate condition '${e}'.`
        );
      }

      if (nodeUtils.isParameterTopGlobal(node)) {
        throw new Error(
          `The global parameter '${nodeUtils.getName(node)}' contains a duplicate condition '${e}'.`
        );
      }

      if (nodeUtils.isParameterNestedGlobal(node)) {
        throw new Error(
          `The global nested parameter '${nodeUtils.getName(
            node
          )}' contains a duplicate condition '${e}'.`
        );
      }

      if (nodeUtils.isParameterTopLocal(node)) {
        throw new Error(
          `The function parameter '${nodeUtils.getName(
            node
          )}' contains a duplicate condition '${e}'.`
        );
      }

      if (nodeUtils.isParameterNestedLocal(node)) {
        throw new Error(
          `The function nested parameter '${nodeUtils.getName(
            node
          )}' contains a duplicate condition '${e}'.`
        );
      }

      throw new Error(`Node '${nodeUtils.getName(node)}' contains a duplicate condition '${e}'.`);
    }

    _namesSet.add(e);
  });
};

const validateNode = (node, parentId) => {
  const _name = nodeUtils.getName(node);

  if (!_name) {
    throw new Error(
      `A child node of '${pathUtils.getName(
        parentId
      )}' has an unrecognized type or its name is missing.\n\n---\n\n${YAML.stringify(node)}`
    );
  }

  if (_name.startsWith(":")) {
    throw new Error(`The node name '${_name}' cannot start with a colon.`);
  } else if (_name.endsWith(":")) {
    throw new Error(`The node name '${_name}' cannot end with a colon.`);
  } else if (_name.includes("::")) {
    throw new Error(`The node name '${_name}' cannot contain a double colon.`);
  }
};

//----------------------------------------------------------------------------------

const updateReferenceCount = (globals, container) => {
  const _parametersLinkedGlobals = globals[typeUtils.getTypeParameterLinked()] ?? [];
  const _parametersLinkedFunctions = container[typeUtils.getTypeParameterLinked()] ?? [];

  const _parametersLinked = [..._parametersLinkedGlobals, ..._parametersLinkedFunctions];

  _parametersLinked.forEach((e) => {
    const _idTarget = nodeUtils.getIdTarget(e);
    const _nodeTarget = getGlobalById(globals, _idTarget);

    if (!_nodeTarget) {
      throw Error(`The linked parameter does not exist: ${nodeUtils.getID(e)} - ${_idTarget}.`);
    }

    nodeUtils.addIdReference(_nodeTarget, nodeUtils.getID(e));
  });
};

const getGlobalById = (globals, id) => {
  const _parameters = globals[typeUtils.getTypeParameter()] ?? [];

  return _parameters.find((e) => nodeUtils.getID(e) === id);
};

//----------------------------------------------------------------------------------

const handleGlobalParameters = (globals) => {
  const _parameters = globals[typeUtils.getTypeParameter()] ?? [];

  _parameters.forEach((e) => nodeUtils.setType(e, typeUtils.getTypeParameterGlobal()));
};

const handleLinkedParameters = (globals, container) => {
  const _parametersLinkedGlobals = globals[typeUtils.getTypeParameterLinked()] ?? [];
  const _parametersLinkedFunctions = container[typeUtils.getTypeParameterLinked()] ?? [];

  const _parametersLinked = [..._parametersLinkedGlobals, ..._parametersLinkedFunctions];

  _parametersLinked.forEach((e) => {
    const _idTarget = nodeUtils.getIdTarget(e);
    const _idTargetValue = pathUtils.getIdElement(
      _idTarget,
      typeUtils.getTypeParameter(),
      pathUtils.getIdMarkerRoot()
    );

    nodeUtils.setIdTarget(e, _idTargetValue);

    let _isCorrect = false;

    globals[typeUtils.getTypeParameter()].forEach((f) => {
      if (_idTargetValue === nodeUtils.getID(f)) {
        _isCorrect = true;
      }
    });

    if (!_isCorrect) {
      throw new Error(
        `The parameter '${nodeUtils.getName(
          e
        )}' is linked to a global parameter '${pathUtils.getName(_idTarget)}' which does not exist.`
      );
    }
  });
};
